import { MapPin, Phone, Mail, Clock, Instagram, Facebook, PawPrint, ArrowUpRight } from "lucide-react";
import { useLang } from "../LanguageContext";
import { CONTACT } from "../content";
import { Reveal } from "./Reveal";

export const Contact = () => {
  const { t } = useLang();
  const c = t.contact;

  return (
    <section id="contact" className="bg-ivory py-24 md:py-36" data-testid="contact-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <div>
            <Reveal>
              <p className="text-olive text-xs uppercase tracking-[0.35em] mb-6">{c.chapter}</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-brown text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                {c.title}
              </h2>
            </Reveal>

            <div className="mt-12 space-y-11">
              <Reveal delay={0.15}>
                <div className="flex gap-5">
                  <MapPin className="text-gold shrink-0 mt-1" size={22} strokeWidth={1.5} />
                  <div>
                    <p className="text-olive text-xs uppercase tracking-[0.25em] mb-2">{c.addressLabel}</p>
                    <p className="text-brown text-lg font-light leading-relaxed max-w-xs">{CONTACT.address}</p>
                  </div>
                </div>
              </Reveal>

              <Reveal delay={0.2}>
                <div className="flex gap-5">
                  <Phone className="text-gold shrink-0 mt-1" size={22} strokeWidth={1.5} />
                  <div>
                    <p className="text-olive text-xs uppercase tracking-[0.25em] mb-2">{c.phoneLabel}</p>
                    <a href={CONTACT.phoneHref} className="text-brown text-lg font-light hover:text-gold transition-colors block" data-testid="phone-link">
                      {CONTACT.phone}
                    </a>
                    <a href={CONTACT.phone2Href} className="text-brown text-lg font-light hover:text-gold transition-colors block" data-testid="phone-link-2">
                      {CONTACT.phone2}
                    </a>
                  </div>
                </div>
              </Reveal>

              <Reveal delay={0.2}>
                <div className="flex gap-5">
                  <Mail className="text-gold shrink-0 mt-1" size={22} strokeWidth={1.5} />
                  <div>
                    <p className="text-olive text-xs uppercase tracking-[0.25em] mb-2">{c.emailLabel}</p>
                    <a href={CONTACT.emailHref} className="text-brown text-lg font-light hover:text-gold transition-colors break-all" data-testid="email-link">
                      {CONTACT.email}
                    </a>
                  </div>
                </div>
              </Reveal>

              <Reveal delay={0.25}>
                <div className="flex gap-5">
                  <Clock className="text-gold shrink-0 mt-1" size={22} strokeWidth={1.5} />
                  <div>
                    <p className="text-olive text-xs uppercase tracking-[0.25em] mb-2">{c.hoursLabel}</p>
                    {c.hours.map((h, i) => (
                      <p key={i} className="text-brown text-lg font-light">{h}</p>
                    ))}
                    <p className="text-olive text-xs uppercase tracking-[0.25em] mb-2 mt-5">{c.kitchenLabel}</p>
                    <p className="text-brown text-lg font-light" data-testid="kitchen-hours">{c.kitchen}</p>
                  </div>
                </div>
              </Reveal>

              <Reveal delay={0.3}>
                <div className="pt-4 flex items-center gap-4">
                  <span className="text-olive text-xs uppercase tracking-[0.25em]">{t.footer.follow}</span>
                  <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="contact-instagram" className="text-brown/70 hover:text-gold transition-colors"><Instagram size={22} strokeWidth={1.5} /></a>
                  <a href={CONTACT.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="contact-facebook" className="text-brown/70 hover:text-gold transition-colors"><Facebook size={22} strokeWidth={1.5} /></a>
                </div>
              </Reveal>

              <Reveal delay={0.35}>
                <div className="flex items-start gap-4 pt-4 border-t border-sand" data-testid="pet-friendly">
                  <PawPrint className="text-gold shrink-0 mt-0.5" size={20} strokeWidth={1.5} />
                  <div>
                    <p className="text-brown text-sm font-medium tracking-wide">{t.pet.title}</p>
                    <p className="text-brown/55 text-sm font-light mt-0.5">{t.pet.text}</p>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>

          <Reveal delay={0.2} y={50}>
            <div className="relative overflow-hidden rounded-xl border border-sand aspect-square lg:aspect-[4/5] shadow-lg shadow-brown/10">
              <iframe
                title="Restaurante São João location"
                src={CONTACT.mapsEmbed}
                className="absolute inset-0 h-full w-full grayscale-[0.2]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="google-map"
              />
              <a
                href={CONTACT.maps}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="directions-link"
                className="absolute bottom-4 left-4 inline-flex items-center gap-1.5 bg-ivory/95 backdrop-blur text-brown rounded-full px-5 py-2.5 text-xs tracking-wide font-medium transition-colors hover:bg-ivory"
              >
                {c.directions} <ArrowUpRight size={14} />
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
